import { useQuery } from '@apollo/client';
import { isEmpty, isNil } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { Queries } from '@shared/api';
import { RelatedEntity } from '@shared/data/entity/fields/RelatedEntityField';
import { FieldRecord } from '@shared/scenes/entity/types';
import { RelatedCardComponent } from '@shared/scenes/workflows/config';
import { useWorkflowTemplate } from '@shared/scenes/workflows/hooks';
import { API } from '@shared/types/api';
import { notNil } from '@shared/util/notNil';
import { omitTypeName } from '@shared/util/omitTypeName';
import { getEntityFieldMatch } from './getEntityFieldMatch';
import { useFilterByComponents } from './useFilterByComponents';

const PAGE_SIZE = 50;

export function useRelatedEntitySearch(
  relatedCardComponent: RelatedCardComponent,
  fields: FieldRecord,
): {
  keyword: string;
  onSearch: (keyword: string) => void;
  searchResults: RelatedEntity[];
  reset: VoidFunction;
  /** Reloads the current search, e.g. after a card is created that it should now include. */
  refetch: () => Promise<unknown>;
  loading: boolean;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  secondaryTextById: Map<string, string>;
  workflowTemplate?: API.WorkflowTemplateFragment;
} {
  const { workflowTemplateId } = relatedCardComponent;
  const [_keyword, setKeyword] = useState<string>('');
  const [keyword] = useDebounce(_keyword, 300);
  const { workflowTemplate, loading: isWorkflowTemplateLoading } =
    useWorkflowTemplate(workflowTemplateId);
  const sortByComponents = useSortByComponents(relatedCardComponent);
  const filterByComponents = useFilterByComponents({
    workflowTemplate,
    fields,
    relatedCardComponent,
  });

  const variables = useMemo(
    () => ({
      input: {
        workflowTemplateId: workflowTemplateId ?? '',
        limit: PAGE_SIZE,
        cursor: '',
        sortByComponents,
        filterByComponents,
        omniSearchQuery: !isEmpty(keyword) ? keyword : null,
      },
    }),
    [workflowTemplateId, keyword, sortByComponents, filterByComponents],
  );

  const {
    data: listEntities,
    previousData,
    loading: isEntitiesLoading,
    refetch,
    fetchMore,
  } = useQuery<API.ListWorkflowEntitiesQuery, API.ListWorkflowEntitiesQueryVariables>(
    Queries.workflow.listWorkflowEntities,
    {
      variables,
      skip: isNil(workflowTemplateId) || isWorkflowTemplateLoading,
      fetchPolicy: 'cache-and-network',
    },
  );

  useEffect(() => {
    refetch();
  }, [refetch]);

  // While a new search is in flight, `data` is briefly undefined, which would
  // flash the empty state. Fall back to the previous results until the new
  // ones arrive.
  const list = listEntities?.list ?? previousData?.list;

  const searchResults: RelatedEntity[] = useMemo(() => {
    return list?.entities?.filter(notNil) ?? [];
  }, [list?.entities]);

  const secondaryTextById = useMemo(() => {
    const map = new Map<string, string>();
    if (isEmpty(keyword)) {
      return map;
    }
    searchResults.forEach(entity => {
      const fieldMatch = getEntityFieldMatch(entity, keyword, workflowTemplate);
      if (!isNil(fieldMatch)) {
        map.set(entity.id, `${fieldMatch.label}: ${fieldMatch.snippet}`);
      }
    });
    return map;
  }, [searchResults, keyword, workflowTemplate]);

  const reset = useCallback(() => setKeyword(''), [setKeyword]);

  const loadMore = useCallback(async () => {
    if (list?.hasMore && list.cursor) {
      await fetchMore({
        variables: {
          ...variables,
          input: {
            ...variables.input,
            cursor: list.cursor,
          },
        },
      });
    }
  }, [list, fetchMore, variables]);

  return {
    keyword,
    reset,
    refetch,
    onSearch: setKeyword,
    searchResults,
    loading: isWorkflowTemplateLoading || isEntitiesLoading,
    loadMore,
    hasMore: !!list?.hasMore,
    secondaryTextById,
    workflowTemplate,
  };
}

function useSortByComponents(component: RelatedCardComponent): API.SortByComponent[] {
  return useMemo(() => {
    const defaultSort: API.SortByComponent = {
      componentId: 'updatedAt',
      direction: 'DESC',
    };

    return (
      component.listDefault?.sortByComponents?.filter(notNil).map(omitTypeName) ?? [defaultSort]
    );
  }, [component.listDefault?.sortByComponents]);
}
